/**
 * ThemeResolverContext
 * 
 * Wraps the app and dynamically resolves the active theme based on:
 * - Current route (module detection)
 * - Current user role
 * - Program-level theme settings (from AppThemeContext)
 * 
 * Re-applies CSS variables when module/role context changes.
 */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import { useAppTheme } from '@/modules/shared/contexts/AppThemeContext';
import { colorTokenToCss, colorTokenToHslComponents } from '@/modules/shared/utils/colorTokens';
import {
  type ThemeTokens,
  type ThemeLayerConfig,
  type ResolvedTheme,
  detectModuleFromPath,
  resolveTheme,
  loadLayerConfig,
  saveLayerConfig,
  getDefaultLayerConfig,
  getSystemDarkModePreference,
  onDarkModeChange,
} from '@/modules/shared/utils/themeResolver';

// ─────────────────────────────────────────────────────────────────────────────
// Context Type
// ─────────────────────────────────────────────────────────────────────────────

interface ThemeResolverContextType {
  resolvedTheme: ResolvedTheme;
  layerConfig: ThemeLayerConfig;
  currentModule: string | null;
  isDarkMode: boolean;
  systemPrefersDark: boolean;
  updateLayerConfig: (config: ThemeLayerConfig) => void;
  resetLayerConfig: () => void;
}

const ThemeResolverCtx = createContext<ThemeResolverContextType | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
// Apply resolved tokens to CSS
// ─────────────────────────────────────────────────────────────────────────────

function applyResolvedTokens(tokens: ThemeTokens) {
  const root = document.documentElement;

  if (tokens.primaryColor) {
    root.style.setProperty('--app-primary', colorTokenToCss(tokens.primaryColor, 'primary'));
    root.style.setProperty('--primary', colorTokenToHslComponents(tokens.primaryColor, 'primary', root));
  }
  if (tokens.secondaryColor) {
    root.style.setProperty('--app-secondary', colorTokenToCss(tokens.secondaryColor, 'textMuted'));
  }
  if (tokens.accentColor) {
    root.style.setProperty('--app-accent', colorTokenToCss(tokens.accentColor, 'warning'));
  }
  if (tokens.successColor) {
    root.style.setProperty('--app-success', colorTokenToCss(tokens.successColor, 'success'));
  }
  if (tokens.warningColor) {
    root.style.setProperty('--app-warning', colorTokenToCss(tokens.warningColor, 'warning'));
  }
  if (tokens.errorColor) {
    root.style.setProperty('--app-error', colorTokenToCss(tokens.errorColor, 'danger'));
  }
  if (tokens.backgroundColor) {
    root.style.setProperty('--app-background', colorTokenToCss(tokens.backgroundColor, 'background'));
  }
  if (tokens.surfaceColor) {
    root.style.setProperty('--app-surface', colorTokenToCss(tokens.surfaceColor, 'card'));
  }
  if (tokens.textPrimaryColor) {
    root.style.setProperty('--app-text-primary', colorTokenToCss(tokens.textPrimaryColor, 'textPrimary'));
  }
  if (tokens.textSecondaryColor) {
    root.style.setProperty('--app-text-secondary', colorTokenToCss(tokens.textSecondaryColor, 'textMuted'));
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export function ThemeResolverProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user } = useAuth();
  const { settings: appThemeSettings } = useAppTheme();

  const [layerConfig, setLayerConfig] = useState<ThemeLayerConfig>(loadLayerConfig);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemDarkModePreference);

  // Detect current module from route
  const currentModule = detectModuleFromPath(location.pathname);
  const currentRole = user?.programRoleName || user?.role || null;

  // Build program-level tokens from AppTheme settings
  const programTheme: ThemeTokens = {
    primaryColor: appThemeSettings?.primaryColor || 'primary',
    secondaryColor: appThemeSettings?.secondaryColor || 'textMuted',
    accentColor: appThemeSettings?.accentColor || 'warning',
    successColor: appThemeSettings?.successColor || 'success',
    warningColor: appThemeSettings?.warningColor || 'warning',
    errorColor: appThemeSettings?.errorColor || 'danger',
    backgroundColor: appThemeSettings?.backgroundColor || 'background',
    surfaceColor: appThemeSettings?.surfaceColor || 'card',
    textPrimaryColor: appThemeSettings?.textPrimaryColor || 'textPrimary',
    textSecondaryColor: appThemeSettings?.textSecondaryColor || 'textMuted',
  };

  // Resolve theme
  const resolvedTheme = resolveTheme({
    currentModule,
    currentRole,
    programTheme,
    layerConfig,
  });

  // Apply resolved theme CSS variables when resolution changes
  useEffect(() => {
    // Only apply module/role overrides if there are enabled layer themes
    const hasActiveOverrides = layerConfig.moduleThemes.some(m => m.enabled) ||
                               layerConfig.roleThemes.some(r => r.enabled);
    if (hasActiveOverrides) {
      applyResolvedTokens(resolvedTheme.tokens);
    }
  }, [resolvedTheme.tokens, resolvedTheme.source, resolvedTheme.sourceLabel, layerConfig]);

  // Listen for system dark mode changes
  useEffect(() => {
    return onDarkModeChange(setSystemPrefersDark);
  }, []);

  // Compute actual dark mode
  const isDarkMode = layerConfig.darkModeAuto
    ? systemPrefersDark
    : (appThemeSettings?.darkModeEnabled ?? false);

  // Apply dark mode class
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  const updateLayerConfig = useCallback((config: ThemeLayerConfig) => {
    setLayerConfig(config);
    saveLayerConfig(config);
  }, []);

  const resetLayerConfig = useCallback(() => {
    const defaults = getDefaultLayerConfig();
    setLayerConfig(defaults);
    saveLayerConfig(defaults);
  }, []);

  return (
    <ThemeResolverCtx.Provider
      value={{
        resolvedTheme,
        layerConfig,
        currentModule,
        isDarkMode,
        systemPrefersDark,
        updateLayerConfig,
        resetLayerConfig,
      }}
    >
      {children}
    </ThemeResolverCtx.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useThemeResolver() {
  const context = useContext(ThemeResolverCtx);
  if (context === undefined) {
    throw new Error('useThemeResolver must be used within a ThemeResolverProvider');
  }
  return context;
}
